import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { noop } from 'lodash/fp';
import { FormSpacer } from 'components';
import { FormDataType } from 'contexts/FormProvider';
import Dependents from '../components/Dependents';
import { useContentContext } from '@qbe/dfc-content';
import { TITLELISTS, LANGUAGELISTS } from 'pages/Complaint/ConsentForm/enum';
import { YesNoList } from 'utils/constants';
import { FirstnameLastname, DropdownController, TextFieldController, ToggleController } from 'controllers';
import { Button, FormGroupLabel } from '@qbe/ads-core';
import { FormCard } from '@qbe/ads-blocks-forms';
import { useContentComplaintPartners } from 'utils/content';

interface PersonalDetailsProps {
  form: UseFormReturn<FormDataType>;
  isFieldRequired: (fieldName: keyof FormDataType) => boolean;
  showNextButton: boolean;
  enableNextButton: boolean;
  onSubmit: () => void;
}
const PersonalDetails = ({
  form,
  isFieldRequired,
  showNextButton = true,
  enableNextButton = false,
  onSubmit = noop,
}: PersonalDetailsProps) => {
  const {
    control,
    formState: { errors },
    watch,
  } = form;

  const { getTextContent } = useContentComplaintPartners({ context: useContentContext() });
  const isOtherLanguage = watch('language') === 'Other';
  const isLangInterpreter = watch('isLanguageInterpreter') || '';

  const isDependentValid =
    typeof errors.dependents === 'undefined' || (Array.isArray(errors.dependents) && errors.dependents.length === 0);

  const isValid = enableNextButton && isDependentValid;

  return (
    <>
      <FormCard variant="sectionTitle" title={getTextContent('insured.personalDetails.section')}>
        <FormGroupLabel alignCenter legendElementOveride title={getTextContent('fha.insured.personalDetails.title')} />
        <FormSpacer type="input" />
        <DropdownController
          control={control}
          label="Title"
          name="title"
          capture
          optionList={TITLELISTS.map((value) => {
            return {
              label: value,
              value,
            };
          })}
          data-test="title"
        />

        <FirstnameLastname
          form={form}
          maxLength={50}
          slotProps={{
            titleField: {
              title: getTextContent('fha.insured.personalDetails.name'),
            },
            firstnameField: {
              name: 'insuredFirstName',
            },
            lastnameField: {
              name: 'insuredLastName',
            },
          }}
        />

        <Dependents form={form} errorMessage={''} />

        <ToggleController
          control={control}
          name="isLanguageInterpreter"
          required
          title={getTextContent('insured.otherDetails.languageInterpreter')}
          options={YesNoList}
          capture
        />

        {isLangInterpreter === 'yes' && (
          <>
            <FormGroupLabel
              alignCenter
              required={isFieldRequired('isLanguageInterpreter')}
              legendElementOveride
              title={getTextContent('insured.otherDetails.language')}
            />
            <FormSpacer type="input" />
            <DropdownController
              control={control}
              label="Language"
              name="language"
              optionList={LANGUAGELISTS.map((value) => {
                return {
                  label: value,
                  value,
                };
              })}
              data-test="language"
            />
          </>
        )}

        {isOtherLanguage && isLangInterpreter === 'yes' && (
          <>
            <FormGroupLabel
              alignCenter
              required={isFieldRequired('otherLanguage')}
              legendElementOveride
              title={getTextContent('insured.otherDetails.languageType')}
            />
            <FormSpacer type="input" />
            <TextFieldController
              name="otherLanguage"
              control={control}
              maxLength={50}
              label="Other language"
              inputProps={{
                maxLength: 50,
                minLength: 3,
              }}
              data-test="otherLanguage"
            />
          </>
        )}
        <FormSpacer type="formgroup" />

        {showNextButton && <Button type="button" label="Next" disabled={!isValid} onClick={onSubmit} />}
      </FormCard>
    </>
  );
};

export default PersonalDetails;
